import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CommissionTier {
  id: string;
  min_amount: number;
  max_amount: number | null;
  commission_rate: number;
  is_active: boolean;
}

const DEFAULT_COMMISSION_RATE = 20;

export const useCommissionRate = () => {
  const [tiers, setTiers] = useState<CommissionTier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const { data, error } = await supabase
          .from("commission_tiers")
          .select("*")
          .eq("is_active", true)
          .order("min_amount", { ascending: true });

        if (error) {
          console.error("Error fetching commission tiers:", error);
          return;
        }

        setTiers(data || []);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTiers();
  }, []);

  const getCommissionRate = useCallback(
    (amount: number): number => {
      if (tiers.length === 0) {
        return DEFAULT_COMMISSION_RATE;
      }

      // Find the matching tier for the given amount
      const matchingTier = tiers.find((tier) => {
        const minMatch = amount >= tier.min_amount;
        const maxMatch = tier.max_amount === null || amount <= tier.max_amount;
        return minMatch && maxMatch;
      });

      return matchingTier?.commission_rate ?? DEFAULT_COMMISSION_RATE;
    },
    [tiers]
  );

  const calculatePriceWithCommission = useCallback(
    (basePrice: number, amount: number): number => {
      const rate = getCommissionRate(amount);
      const commission = basePrice * (rate / 100);
      return basePrice + commission;
    },
    [getCommissionRate]
  );

  const calculateNetProfit = useCallback(
    (totalUsd: number, amount: number): number => {
      const rate = getCommissionRate(amount);
      // Reverse calculation: profit = total * (rate / (100 + rate))
      return totalUsd * (rate / (100 + rate));
    },
    [getCommissionRate]
  );

  return {
    tiers,
    loading,
    getCommissionRate,
    calculatePriceWithCommission,
    calculateNetProfit,
    defaultRate: DEFAULT_COMMISSION_RATE,
  };
};
